import React, { useState } from "react";
import { useFormik } from "formik";
import * as Yup from "yup";
import {
  Avatar,
  Box,
  Button,
  Divider,
  Grid,
  Paper,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import { toast } from "react-toastify";
import { UserApi } from "../../apis";
import { PageHeader } from "../../components/ui";
import {
  getStoredUser,
  getDisplayName,
  getInitials,
  getRoleName,
} from "../../utils/auth";

const profileSchema = Yup.object({
  name: Yup.string().trim().required("Name is required"),
  email: Yup.string().trim().email("Enter a valid email").required("Email is required"),
});

const passwordSchema = Yup.object({
  currentPassword: Yup.string().required("Enter your current password"),
  newPassword: Yup.string()
    .min(8, "Use at least 8 characters")
    .required("New password is required"),
  confirmPassword: Yup.string()
    .oneOf([Yup.ref("newPassword")], "Passwords do not match")
    .required("Confirm the new password"),
});

/**
 * The signed-in user's own profile.
 *
 * GET /api/user/ reads req.session.userId, but the app authenticates with a
 * JWT and never populates that session, so it always 404s. The stored login
 * payload is the reliable source here; saving writes back through
 * /api/user/update and refreshes what the shell reads.
 */
export default function Profile() {
  const stored = getStoredUser();
  const [user, setUser] = useState(stored);

  const persist = (next) => {
    try {
      localStorage.setItem("user", JSON.stringify(next));
    } catch {
      // Non-fatal: the header just shows the old name until the next login.
    }
    setUser(next);
  };

  const profileForm = useFormik({
    enableReinitialize: true,
    initialValues: { name: user?.name || "", email: user?.email || "" },
    validationSchema: profileSchema,
    onSubmit: async (values, { setSubmitting }) => {
      try {
        const res = await UserApi.updateUser({ id: user.id, ...values });
        const updated = res?.data?.data?.user;
        persist({ ...user, ...values, ...(updated || {}) });
        toast.success("Profile updated");
      } catch (error) {
        console.error("Failed to update profile:", error);
        toast.error(error.response?.data?.message || "Could not update your profile.");
      } finally {
        setSubmitting(false);
      }
    },
  });

  const passwordForm = useFormik({
    initialValues: { currentPassword: "", newPassword: "", confirmPassword: "" },
    validationSchema: passwordSchema,
    onSubmit: async (values, { setSubmitting, resetForm }) => {
      try {
        await UserApi.changePassword({
          id: user.id,
          currentPassword: values.currentPassword,
          newPassword: values.newPassword,
        });
        toast.success("Password changed");
        resetForm();
      } catch (error) {
        console.error("Failed to change password:", error);
        toast.error(error.response?.data?.message || "Could not change your password.");
      } finally {
        setSubmitting(false);
      }
    },
  });

  if (!user) {
    return (
      <Box>
        <PageHeader title="Profile" />
        <Typography color="text.secondary">You are not signed in.</Typography>
      </Box>
    );
  }

  const cardSx = {
    p: { xs: 2, md: 3 },
    mb: 3,
    mx: { xs: -2, md: 0 },
    borderRadius: { xs: 0, md: 1 },
  };

  return (
    <Box>
      <PageHeader title="Profile" />

      <Paper elevation={3} sx={cardSx}>
        <Stack
          direction={{ xs: "column", sm: "row" }}
          spacing={2}
          alignItems={{ xs: "flex-start", sm: "center" }}
          sx={{ mb: 3 }}
        >
          <Avatar
            sx={{
              width: 64,
              height: 64,
              fontSize: "1.25rem",
              fontWeight: 700,
              bgcolor: "primary.main",
              color: "primary.contrastText",
            }}
          >
            {getInitials(user)}
          </Avatar>
          <Box sx={{ minWidth: 0 }}>
            <Typography variant="h5" sx={{ fontWeight: 600 }} noWrap>
              {getDisplayName(user)}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {getRoleName(user) || "No role"} · {user.email}
            </Typography>
          </Box>
        </Stack>

        <Divider sx={{ mb: 3 }} />

        <Box component="form" onSubmit={profileForm.handleSubmit}>
          <Grid container rowSpacing={3} columnSpacing={{ xs: 0, md: 3 }}>
            <Grid item xs={12} md={6}>
              <Typography variant="subtitle2" gutterBottom fontWeight={600}>
                Name
              </Typography>
              <TextField
                fullWidth
                name="name"
                value={profileForm.values.name}
                onChange={profileForm.handleChange}
                onBlur={profileForm.handleBlur}
                error={profileForm.touched.name && Boolean(profileForm.errors.name)}
                helperText={profileForm.touched.name && profileForm.errors.name}
              />
            </Grid>

            <Grid item xs={12} md={6}>
              <Typography variant="subtitle2" gutterBottom fontWeight={600}>
                Email
              </Typography>
              <TextField
                fullWidth
                name="email"
                type="email"
                value={profileForm.values.email}
                onChange={profileForm.handleChange}
                onBlur={profileForm.handleBlur}
                error={profileForm.touched.email && Boolean(profileForm.errors.email)}
                helperText={profileForm.touched.email && profileForm.errors.email}
              />
            </Grid>

            <Grid item xs={12}>
              <Stack
                direction={{ xs: "column-reverse", sm: "row" }}
                spacing={2}
                justifyContent="flex-end"
              >
                <Button
                  variant="outlined"
                  onClick={() => profileForm.resetForm()}
                  disabled={profileForm.isSubmitting || !profileForm.dirty}
                >
                  Reset
                </Button>
                <Button
                  type="submit"
                  variant="contained"
                  disabled={profileForm.isSubmitting || !profileForm.dirty}
                  sx={{ minWidth: 140 }}
                >
                  {profileForm.isSubmitting ? "Saving..." : "Save changes"}
                </Button>
              </Stack>
            </Grid>
          </Grid>
        </Box>
      </Paper>

      <Paper elevation={3} sx={cardSx}>
        <Typography variant="h6" gutterBottom fontWeight={600}>
          Change password
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
          Confirm your current password, then choose a new one of at least 8 characters.
        </Typography>

        <Box component="form" onSubmit={passwordForm.handleSubmit}>
          <Grid container rowSpacing={3} columnSpacing={{ xs: 0, md: 3 }}>
            <Grid item xs={12}>
              <Typography variant="subtitle2" gutterBottom fontWeight={600}>
                Current password
              </Typography>
              <TextField
                fullWidth
                name="currentPassword"
                type="password"
                autoComplete="current-password"
                value={passwordForm.values.currentPassword}
                onChange={passwordForm.handleChange}
                onBlur={passwordForm.handleBlur}
                error={
                  passwordForm.touched.currentPassword &&
                  Boolean(passwordForm.errors.currentPassword)
                }
                helperText={
                  passwordForm.touched.currentPassword && passwordForm.errors.currentPassword
                }
                sx={{ maxWidth: { md: "calc(50% - 12px)" } }}
              />
            </Grid>

            <Grid item xs={12} md={6}>
              <Typography variant="subtitle2" gutterBottom fontWeight={600}>
                New password
              </Typography>
              <TextField
                fullWidth
                name="newPassword"
                type="password"
                autoComplete="new-password"
                value={passwordForm.values.newPassword}
                onChange={passwordForm.handleChange}
                onBlur={passwordForm.handleBlur}
                error={
                  passwordForm.touched.newPassword && Boolean(passwordForm.errors.newPassword)
                }
                helperText={passwordForm.touched.newPassword && passwordForm.errors.newPassword}
              />
            </Grid>

            <Grid item xs={12} md={6}>
              <Typography variant="subtitle2" gutterBottom fontWeight={600}>
                Confirm new password
              </Typography>
              <TextField
                fullWidth
                name="confirmPassword"
                type="password"
                autoComplete="new-password"
                value={passwordForm.values.confirmPassword}
                onChange={passwordForm.handleChange}
                onBlur={passwordForm.handleBlur}
                error={
                  passwordForm.touched.confirmPassword &&
                  Boolean(passwordForm.errors.confirmPassword)
                }
                helperText={
                  passwordForm.touched.confirmPassword && passwordForm.errors.confirmPassword
                }
              />
            </Grid>

            <Grid item xs={12}>
              <Stack direction="row" justifyContent="flex-end">
                <Button
                  type="submit"
                  variant="contained"
                  disabled={passwordForm.isSubmitting}
                  sx={{ minWidth: 180, width: { xs: "100%", sm: "auto" } }}
                >
                  {passwordForm.isSubmitting ? "Updating..." : "Update password"}
                </Button>
              </Stack>
            </Grid>
          </Grid>
        </Box>
      </Paper>
    </Box>
  );
}
